/**
 * Retrieves the translation of text.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-i18n/
 */
import { __ } from '@wordpress/i18n';

/**
 * React hook that is used to mark the block wrapper element.
 * It provides all the necessary props like the class name.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-block-editor/#useblockprops
 */
import {
	 useBlockProps
} from '@wordpress/block-editor';

import { Button } from '@wordpress/components'; 

/**
 * Lets webpack process CSS, SASS or SCSS files referenced in JavaScript files.
 * Those files can contain any CSS code that gets applied to the editor.
 *
 * @see https://www.npmjs.com/package/@wordpress/scripts#using-css
 */
import './editor.scss';

/**
 * Slicpix Interactivity Studio namespace.
 *
 * @namespace wpSlicpixInteractivityStudio
 * @memberof  window
 *
 * @since 1.0.0
 *
 * @property Function onWpInteractivityStudioViewMyImagesClick Event listener for the custom block 'view my images' button.
 */
;(function ( ns, undefined ) {

	/**
	 * Global namespace constant variables.
	 */
	const _domainUri="https://interactivity.studio/"
	const epSendVerfificationEmail = _domainUri + "wp/sessions/create";
	const epSendVerificationCode = _domainUri + "wp/sessions/verify";
	const epConnect = _domainUri + "wp/connect";
	const epArtworks = _domainUri + "plugin/content/user/{account}/artworks";
	const epArtwork = _domainUri + "plugin/content/user/{account}/artwork/{asset}";
	const epInteractive = _domainUri + "content/interactivity/{account}/{asset}";

	const loginTmpl = `<div class="signin" id="wp-signin">
		<div class="fields">
			<div class="navbar-brand">
				<img src="https://interactivity.studio/images/interactivity_studio_logo_transparent.png" alt="" class="logo-img">
				<span>Interactivity Studio</span>
				<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="60" height="52" viewBox="0 0 768 768">
					<path d="M544.5 511.5v96l127.5-127.5-127.5-127.5v96h-225v63h225zM96 288l127.5 127.5v-96h225v-63h-225v-96z"></path>
				</svg>
				<img src="https://interactivity.studio/images/WordPress_blue_logo.webp" alt="wordpress logo" class="wp-logo-img">
				<span>WordPress</span>
			</div>
			<p><span>Your Wordpress site is not yet connected to your Interactivity Studio account.<br>Sign in to continue.</span></p>
			<p><span>ONLY WORDPRESS SITE ADMINISTRATORS CAN ESTABLISH THE CONNECTION</span></p>
			<h2>Sign In</h2>
			<div class="feedback">An email address is required</div>
			<div class="email-signin field" data-state="on">
				<label>Email *</label>
				<input type="text" maxlength="100" tabindex="0">
				<p><span>By signing in, I agree to SlicPix's </span><a href="https://interactivity.studio/legal/privacy-policy" target="_blank">privacy policy</a><span> &amp; </span><a href="https://interactivity.studio/legal/terms-of-service" target="_blank">terms of service</a></p>
			</div>
			<div class="verificationcode-signin field" data-state="off">
				<p style="padding-top:0;"><span>Lets make sure it is really you</span><br><span>Enter the verification code sent to your email</span></p>
				<label>Verification Code</label>
				<input type="text" maxlength="6" tabindex="0" autocomplete="off">
			</div>
			<div class="cmds-cont">
				<div id="signin-next" class="button" tabindex="0">Next</div>
			</div>
		</div>
		<div class="more"></div>
	</div>`;

	const setupSuccessTmpl = `<div class="signin" id="wp-setup-success">
		<div class="fields">
			<div class="navbar-brand">
				<img src="https://interactivity.studio/images/interactivity_studio_logo_transparent.png" alt="" class="logo-img">
				<span>Interactivity Studio</span>
				<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="60" height="52" viewBox="0 0 768 768">
					<path d="M544.5 511.5v96l127.5-127.5-127.5-127.5v96h-225v63h225zM96 288l127.5 127.5v-96h225v-63h-225v-96z"></path>
				</svg>
				<img src="https://interactivity.studio/images/WordPress_blue_logo.webp" alt="wordpress logo" class="wp-logo-img">
				<span>WordPress</span>
			</div>
			<h2>Congratulations</h2>
			<p style="padding-top:0;"><span>Your WordPress site is now connected</span></p>
			<div class="cmds-cont">
				<div id="wp-continue" class="button" tabindex="0" data-state="active">Continue</div>
			</div>
		</div>
		<div class="more"></div>
	</div>`;

	const setupFailureTmpl = `<div class="signin" id="wp-setup-failure">
		<div class="fields">
			<div class="navbar-brand">
				<img src="https://interactivity.studio/images/interactivity_studio_logo_transparent.png" alt="" class="logo-img">
				<span>Interactivity Studio</span>
				<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="60" height="52" viewBox="0 0 768 768">
					<path d="M544.5 511.5v96l127.5-127.5-127.5-127.5v96h-225v63h225zM96 288l127.5 127.5v-96h225v-63h-225v-96z"></path>
				</svg>
				<img src="https://interactivity.studio/images/WordPress_blue_logo.webp" alt="wordpress logo" class="wp-logo-img">
				<span>WordPress</span>
			</div>
			<h2>Connection failure</h2>
			<p style="padding-top:0;"><span>Your WordPress site failed to connect to your Interactivity Studio account</span></p>
			<p style="padding-top:0;"><span>Remedy: click the close button and retry connecting your site by selecting the "view my images" button on an interactive image block or contact our team at</span></p>
			<div class="cmds-cont">
				<div id="wp-close" class="button" tabindex="0" data-state="active">Close</div>
			</div>
		</div>
		<div class="more"></div>
	</div>`;

	const setupConflictTmpl = `<div class="signin" id="wp-setup-coflict">
		<div class="fields">
			<div class="navbar-brand">
				<img src="https://interactivity.studio/images/interactivity_studio_logo_transparent.png" alt="" class="logo-img">
				<span>Interactivity Studio</span>
				<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="60" height="52" viewBox="0 0 768 768">
					<path d="M544.5 511.5v96l127.5-127.5-127.5-127.5v96h-225v63h225zM96 288l127.5 127.5v-96h225v-63h-225v-96z"></path>
				</svg>
				<img src="https://interactivity.studio/images/WordPress_blue_logo.webp" alt="wordpress logo" class="wp-logo-img">
				<span>WordPress</span>
			</div>
			<h2>Connection conflict</h2>
			<p style="padding-top:0;"><span>Your Interactivity Studio account is already connected with a WordPress site. An account can only be connected to one WordPress site.</span></p>
			<p style="padding-top:0;"><span>Please contact us at if you are in the process of re-installing without a backup a WordPress site which was originally connected to your account.<br>Our team is here to help you resolve this particular situation</span></p>
			<div class="cmds-cont">
				<div id="wp-close" class="button" tabindex="0" data-state="active">Close</div>
			</div>
		</div>
		<div class="more"></div>
	</div>`;

	const wp_rest_api_issue_tmpl = `<div class="signin" id="wp-rest_api_issue">
		<div class="fields">
			<div class="navbar-brand">
				<img src="https://interactivity.studio/images/interactivity_studio_logo_transparent.png" alt="" class="logo-img">
				<span>Interactivity Studio</span>
				<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="60" height="52" viewBox="0 0 768 768">
					<path d="M544.5 511.5v96l127.5-127.5-127.5-127.5v96h-225v63h225zM96 288l127.5 127.5v-96h225v-63h-225v-96z"></path>
				</svg>
				<img src="https://interactivity.studio/images/WordPress_blue_logo.webp" alt="wordpress logo" class="wp-logo-img">
				<span>WordPress</span>
			</div>
			<h2>Wordpress Rest API Issue</h2>
			<p style="padding-top:0;"><span>We are unable to interface with your Wordpress Rest API. The reason returned by the API is {code}.</span></p>
			<div class="cmds-cont">
				<div id="wp-close" class="button" tabindex="0" data-state="active">Close</div>
			</div>
		</div>
		<div class="more"></div>
	</div>`;

	const connection_error_tmpl = `<div class="signin" id="wp-connection_error">
		<div class="fields">
			<div class="navbar-brand">
				<img src="https://interactivity.studio/images/interactivity_studio_logo_transparent.png" alt="" class="logo-img">
				<span>Interactivity Studio</span>
				<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="60" height="52" viewBox="0 0 768 768">
					<path d="M544.5 511.5v96l127.5-127.5-127.5-127.5v96h-225v63h225zM96 288l127.5 127.5v-96h225v-63h-225v-96z"></path>
				</svg>
				<img src="https://interactivity.studio/images/WordPress_blue_logo.webp" alt="wordpress logo" class="wp-logo-img">
				<span>WordPress</span>
			</div>
			<h2>You are not connected</h2>
			<p style="padding-top:0;"><span>We are unable to interface with the Interactivity Studio API.</span></p>
			<p style="padding-top:0;"><span>Please check your internet connection and try again.</span></p>
			<div class="cmds-cont">
				<div id="wp-close" class="button" tabindex="0" data-state="active">Close</div>
			</div>
		</div>
		<div class="more"></div>
	</div>`;

	const api_error_tmpl = `<div class="signin" id="wp-api_error">
		<div class="fields">
			<div class="navbar-brand">
				<img src="https://interactivity.studio/images/interactivity_studio_logo_transparent.png" alt="" class="logo-img">
				<span>Interactivity Studio</span>
				<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="60" height="52" viewBox="0 0 768 768">
					<path d="M544.5 511.5v96l127.5-127.5-127.5-127.5v96h-225v63h225zM96 288l127.5 127.5v-96h225v-63h-225v-96z"></path>
				</svg>
				<img src="https://interactivity.studio/images/WordPress_blue_logo.webp" alt="wordpress logo" class="wp-logo-img">
				<span>WordPress</span>
			</div>
			<h2>Interactivity Studio API Error</h2>
			<p style="padding-top:0;"><span>The Interactivity Studio API encountered an issue</span></p>
			<p style="padding-top:0;"><span>Please <a href='https://interactivity.studio' target='_blank'>contact our team</a> via the chat so we can assist you in this matter.</span></p>
			<div class="cmds-cont">
				<div id="wp-close" class="button" tabindex="0" data-state="active">Close</div>
			</div>
		</div>
		<div class="more"></div>
	</div>`;

	const myImagesTmpl = `<div class="my-images" tabindex="-1">
		<nav class="navbar">
			<a class="navbar-brand">
				<img src="https://interactivity.studio/images/interactivity_studio_logo.webp" alt="" class="logo-img">
				<span>Interactivity Studio</span>
			</a>
			<div class="navbar-nav">
				<ul>
					<li class="nav-item" tabindex="0">
						<span class="nav-link">
							<svg viewBox="0 0 18 16">
								<path d="M3.5 2h-3c-0.275 0-0.5 0.225-0.5 0.5v11c0 0.275 0.225 0.5 0.5 0.5h3c0.275 0 0.5-0.225 0.5-0.5v-11c0-0.275-0.225-0.5-0.5-0.5zM3 5h-2v-1h2v1z"></path>
								<path d="M8.5 2h-3c-0.275 0-0.5 0.225-0.5 0.5v11c0 0.275 0.225 0.5 0.5 0.5h3c0.275 0 0.5-0.225 0.5-0.5v-11c0-0.275-0.225-0.5-0.5-0.5zM8 5h-2v-1h2v1z"></path>
								<path d="M11.954 2.773l-2.679 1.35c-0.246 0.124-0.345 0.426-0.222 0.671l4.5 8.93c0.124 0.246 0.426 0.345 0.671 0.222l2.679-1.35c0.246-0.124 0.345-0.426 0.222-0.671l-4.5-8.93c-0.124-0.246-0.426-0.345-0.671-0.222z"></path>
							</svg>
							<a href="https://help.interactivity.studio" target="_blank">tutorials</a>
						</span>
					</li>
				</ul>
			</div>
		</nav>
		<div id="main"></div>
	</div>`;

	const myImagesFooterTmpl = `<div class="my-images-footer" tabindex="-1">
		<div class="inner" tabindex="-1">
			<button id="slicpix-interactivity-studio-refresh" role="tab" class="tab highlight" tabindex="-1">
				<svg viewBox="0 0 16 16">
					<path d="M13.901 2.599c-1.463-1.597-3.565-2.599-5.901-2.599-4.418 0-8 3.582-8 8h1.5c0-3.59 2.91-6.5 6.5-6.5 1.922 0 3.649 0.835 4.839 2.161l-2.339 2.339h5.5v-5.5l-2.099 2.099z"></path>
					<path d="M14.5 8c0 3.59-2.91 6.5-6.5 6.5-1.922 0-3.649-0.835-4.839-2.161l2.339-2.339h-5.5v5.5l2.099-2.099c1.463 1.597 3.565 2.599 5.901 2.599 4.418 0 8-3.582 8-8h-1.5z"></path>
				</svg>
				<span>refresh</span>
			</button>
			<div class="right" tabindex="-1">
				<a href="https://features.interactivity.studio/webflow-video-guide" target="_blank" tabindex="0" aria-label="go to interactivity studio to view our video guide">
					<button role="tab" class="tab highlight" tabindex="-1">
						<svg viewBox="0 0 16 16">
							<path d="M0 2v12h16v-12h-16zM3 13h-2v-2h2v2zM3 9h-2v-2h2v2zM3 5h-2v-2h2v2zM12 13h-8v-10h8v10zM15 13h-2v-2h2v2zM15 9h-2v-2h2v2zM15 5h-2v-2h2v2zM6 5v6l4-3z"></path>
						</svg>
						<span>Video guide</span>
					</button>
				</a>
				<a href="https://help.interactivity.studio" target="_blank" tabindex="0" aria-label="go to help.interactivity.studio">
					<button role="tab" class="tab highlight" tabindex="-1">
						<svg viewBox="0 0 16 16">
							<path d="M15.333 8c0-2.025-0.821-3.859-2.148-5.185s-3.161-2.148-5.185-2.148-3.859 0.821-5.185 2.148-2.148 3.161-2.148 5.185 0.821 3.859 2.148 5.185 3.161 2.148 5.185 2.148 3.859-0.821 5.185-2.148 2.148-3.161 2.148-5.185zM14 8c0 1.657-0.671 3.156-1.757 4.243s-2.585 1.757-4.243 1.757-3.156-0.671-4.243-1.757-1.757-2.585-1.757-4.243 0.671-3.156 1.757-4.243 2.585-1.757 4.243-1.757 3.156 0.671 4.243 1.757 1.757 2.585 1.757 4.243zM6.689 6.221c0.122-0.347 0.373-0.612 0.681-0.76s0.671-0.178 1.019-0.055c0.305 0.107 0.546 0.313 0.7 0.573 0.122 0.205 0.189 0.443 0.191 0.691 0 0.103-0.026 0.206-0.078 0.309-0.053 0.107-0.135 0.217-0.245 0.327-0.473 0.473-1.221 0.728-1.221 0.728-0.349 0.117-0.538 0.494-0.421 0.843s0.494 0.538 0.843 0.421c0 0 1.029-0.337 1.742-1.050 0.186-0.186 0.363-0.409 0.495-0.673 0.133-0.267 0.219-0.572 0.219-0.913-0.003-0.487-0.136-0.958-0.378-1.366-0.309-0.519-0.793-0.935-1.403-1.149-0.695-0.244-1.423-0.183-2.038 0.111s-1.119 0.825-1.363 1.52c-0.123 0.347 0.060 0.728 0.407 0.85s0.728-0.061 0.85-0.407zM8 12c0.368 0 0.667-0.299 0.667-0.667s-0.299-0.667-0.667-0.667-0.667 0.299-0.667 0.667 0.299 0.667 0.667 0.667z"></path>
						</svg>
						<span>Help</span>
					</button>
				</a>
			</div>
		</div>
	</div>`;

	/**
	 * Global namespace modifiable variables.
	 */
	let _apikey, _account, _asset, _gallery, _grid, _interactiveImage, _interactiveData, _dataBubblesDef, _defs;
	let _customBlock ,_blockId, _insertionStatusMsg;

	/**
	 * WP Rest API error feedback overlay generation and display.
	 *
	 * Generates an overlay to report an error encountered upon a call to
	 * the WP Rest API.
	 *
	 * @since      1.0.0
	 * @access     private
	 *
	 * @memberof wpSlicpixInteractivityStudio
	 *
	 * @param {string} status Error code, if any, provided by the WordPress Rest API.
	 */
	const wpInteractivityStudioDisplayWpRestApiIssueScreen = function ( status ) {
		var div = document.createElement('div');
		var issue = wp_rest_api_issue_tmpl.replace( '{code}', status );
		div.innerHTML = issue;
		
		var body = document.querySelector( 'body' );
		var dialog = body.querySelector( '[id="wp-block-slicpix-interactivity-studio-dialog"]' );
		var content_dg = dialog.querySelector( '[id="content-dg"]' );
		content_dg.innerHTML = "";
		content_dg.appendChild( div.querySelector( 'div' ) );

		var close = dialog.querySelector( '[id="wp-close"]' );
		close.addEventListener( 'click', onWpInteractivityStudioCloseDialog, false );

		dialog.setAttribute( 'data-state', 'on' );
	}

	/**
	 * Connectivity issue error feedback overlay generation and display.
	 *
	 * Generates an overlay to report of a connectivity error encountered upon a call to
	 * the Interactivity Studio Rest API.
	 *
	 * @since      1.0.0
	 * @access     private
	 *
	 * @memberof wpSlicpixInteractivityStudio
	 */
	const wpInteractivityStudioDisplayApiConnectionIssueScreen = function () {
		var div = document.createElement( 'div' );
		div.innerHTML = connection_error_tmpl;

		var body = document.querySelector( 'body' );
		var dialog = body.querySelector( '[id="wp-block-slicpix-interactivity-studio-dialog"]' );
		var content_dg = dialog.querySelector( '[id="content-dg"]' );
		content_dg.innerHTML = "";
		content_dg.appendChild( div.querySelector( 'div' ) );

		var close = dialog.querySelector( '[id="wp-close"]' );
		close.addEventListener( 'click', onWpInteractivityStudioCloseDialog , false );

		dialog.setAttribute( 'data-state', 'on' );
	}

	/**
	 * Interactivity Studio Rest API error feedback overlay generation and display.
	 *
	 * Generates an overlay to report of of an error encountered upon a call to
	 * the Interactivity Studio Rest API.
	 *
	 * @since      1.0.0
	 * @access     private
	 *
	 * @memberof wpSlicpixInteractivityStudio
	 */
	const wpInteractivityStudioDisplayApiIssueScreen = function () {
		var div = document.createElement( 'div' );
		div.innerHTML = api_error_tmpl;

		var body = document.querySelector( 'body' );
		var dialog = body.querySelector( '[id="wp-block-slicpix-interactivity-studio-dialog"]' );
		var content_dg = dialog.querySelector( '[id="content-dg"]' );
		content_dg.innerHTML = "";
		content_dg.appendChild( div.querySelector( 'div' ) );

		var close = dialog.querySelector( '[id="wp-close"]' );
		close.addEventListener( 'click', onWpInteractivityStudioCloseDialog , false );

		dialog.setAttribute( 'data-state', 'on' );
	}

	/**
	 * Retrieve the Interactivity Studio installation key.
	 *
	 * Fetch from the WP Options table the Interactivity Studio Installation
	 * UUID key.
	 * If a key is returned, sets the namespace's _apiKey variable with its value
	 * and calls the loadContext function.
	 * If no key is returned, calls the wpInteractivityStudioLoadSignInScreen function.
	 * 
	 * @since      1.0.0
	 * @access     private
	 *
	 * @see loadContext
	 * @see wpInteractivityStudioLoadSignInScreen
	 * @see wpInteractivityStudioDisplayWpRestApiIssueScreen
	 * 
	 * @memberof wpSlicpixInteractivityStudio
	 */
	async function wpInteractivityStudioFetchKey() {
		try{
			await wp.apiFetch( {
				path: 'slicpix_interactivity_studio/key',
			} ).then( data => {
				if ( data.length == 0 ) {
					wpInteractivityStudioLoadSignInScreen();
					var body = document.querySelector( 'body' );
					var dialog = body.querySelector( '[id="wp-block-slicpix-interactivity-studio-dialog"]' );
					dialog.setAttribute( 'data-state', 'on' );
				} else {
					_apikey = data;
					loadContext();
				}
			} );
		} catch ( error ) {
			let code = "";
			if ( error && error.data ) {
				code = error.data.status + ' - code:' +  error.code;
			}
			wpInteractivityStudioDisplayWpRestApiIssueScreen( code );
		}
	}

	/**
	 * Delete the Interactivity Studio installation key.
	 *
	 * Delete from the WP Options table the Interactivity Studio Installation
	 * UUID key.
	 *
	 * @since      1.0.0
	 * @access     private
	 *
	 * @memberof wpSlicpixInteractivityStudio
	 * 
	 * @see wpInteractivityStudioDisplayWpRestApiIssueScreen
	 * 
	 * @param {function} cb	Callback function.
	 */
	async function wpInteractivityStudioDeleteKey( cb ) {
		try {
			await wp.apiFetch( {
				path: 'slicpix_interactivity_studio/key',
				method: 'DELETE'
			} ).then( res => {
				if ( cb ){
					cb();
				}
			} );
		} catch ( error ){
			let code = "";
			if ( error && error.data ) {
				code = error.data.status + " - code:" +  error.code;
			}
			wpInteractivityStudioDisplayWpRestApiIssueScreen( code );
		}
	}

	/**
	 * Sends a verification email.
	 *
	 * Interactivity Studio's usernames are based on email addresses.
	 * Instead of relying on a password for authentication, Interactivity Studio
	 * uses a two steps verification process where the first step is the sending of
	 * an email containing a verification code.
	 * 
	 * @since      1.0.0
	 * @access     private
	 * 
	 * @memberof wpSlicpixInteractivityStudio
	 * 
	 * @see wpInteractivityStudioDisplayWpRestApiIssueScreen
	 * 
	 * @param {object} scope Object containing as properties references to
	 * UI elements.
	 */
	async function wpInteractivityStudioSendVerificationEmail( scope ) {
		try {
			const response = await window.fetch( epSendVerfificationEmail , {
				method: 'POST',
				headers: {
					'content-type': 'application/json;charset=UTF-8',
				},
				body:JSON.stringify( { "email": scope.email.value.trim() } ),
			} );
	
			if ( !response.ok ) {
				scope.feedback.innerText = 'We are experiencing some issues sending the verification email';
				scope.feedback.setAttribute( 'data-state', 'on' );
				return null;
			}
	
			const body = await response.json();
			return body;

		} catch ( error ) {
			scope.feedback.innerText = connection_error;
			scope.feedback.setAttribute( 'data-state', 'on' );
			throw error;
		}
	}

	/**
	 * Collects and verifyes an authentication verification code.
	 *
	 * Interactivity Studio's usernames are based on email addresses.
	 * Instead of relying on a password for authentication, Interactivity Studio
	 * uses a two steps verification process where the second step is the collection
	 * and verification of a code previously sent via email via the Interactivity
	 * Studio API.
	 *
	 * @since      1.0.0
	 * @access     private
	 * 
	 * @memberof wpSlicpixInteractivityStudio
	 * 
	 * @see wpInteractivityStudioDisplayWpRestApiIssueScreen
	 * 
	 * @param {object} scope Object containing as properties references to
	 * UI elements.
	 */
	async function wpInteractivityStudioSendVerificationCode( scope ) {
		try{
			const response = await window.fetch( epSendVerificationCode , {
				method: 'POST',
				headers: {
					'content-type': 'application/json;charset=UTF-8',
				},
				body:JSON.stringify(
				{
					'email' : scope.email.value.trim(),
					'code' : scope.code.value.trim(),
					'session' : scope.session,
					'key' : _apikey
				} )
			} );
		
			if ( !response.ok ) {
				if ( response.status!=409 ){
					return null;
				}else{
					return 'conflict';
				}
			}
		
			const body = await response.json();
			return body;

		} catch ( error ){
			scope.feedback.innerText = connection_error;
			scope.feedback.setAttribute( 'data-state', 'on' );
			throw error;
		}
	}

	/**
	 * Retrieves account information.
	 *
	 * @since      1.0.0
	 * @access     private
	 * 
	 * @memberof wpSlicpixInteractivityStudio
	 * 
	 * @see wpInteractivityStudioDisplayWpRestApiIssueScreen
	 * @see wpInteractivityStudioDisplayApiConnectionIssueScreen
	 */
	async function connect() {
		try {
			const response = await window.fetch( epConnect, {
				method: 'POST',
				headers: {
					'content-type': 'application/json;charset=UTF-8',
				},
				body:JSON.stringify( { 'key': _apikey } ),
			} );
	
			if ( !response.ok ) {
				wpInteractivityStudioDisplayApiIssueScreen();
				return null;
			}
	
			const body = await response.json();
			return body;

		} catch ( error ){
			wpInteractivityStudioDisplayApiConnectionIssueScreen();
			throw error;
		}
	}

	/**
	 * Retrieves interactive images definitions.
	 *
	 * @since      1.0.0
	 * @access     private
	 * 
	 * @memberof wpSlicpixInteractivityStudio
	 * 
	 * @see wpInteractivityStudioDisplayWpRestApiIssueScreen
	 * @see wpInteractivityStudioDisplayApiConnectionIssueScreen
	 */
	async function fetchArtworks() {
		const _ep = epArtworks.replace( '{account}', _account );
		try {
			const response = await window.fetch( _ep, {
				method: 'GET',
				headers: {
					'content-type': 'application/json;charset=UTF-8',
				}
			} );
	
			if ( !response.ok ) {
				if ( response.status != 404 ) {
					wpInteractivityStudioDisplayApiIssueScreen();
				}
				return null;
			}
	
		const body = await response.json();
		return body;

		} catch (error){
			wpInteractivityStudioDisplayApiConnectionIssueScreen();
		}
	}

	/**
	 * Retrieves an interactive image's definition.
	 *
	 * @since      1.0.0
	 * @access     private
	 * 
	 * @memberof wpSlicpixInteractivityStudio
	 * 
	 * @see wpInteractivityStudioDisplayWpRestApiIssueScreen
	 * @see wpInteractivityStudioDisplayApiConnectionIssueScreen
	 */
	async function fetchArtwork() {
		const _ep = epArtwork.replace( '{account}', _account ).replace( '{asset}', _asset );
		try {
			const response = await window.fetch( _ep, {
				method: 'GET',
				headers: {
					'content-type': 'application/json;charset=UTF-8',
				}
			} );
	
			if ( !response.ok ) {
				if ( response.status != 404 ){
					wpInteractivityStudioDisplayApiIssueScreen();
				}
				return null;
			}
	
			const body = await response.json();
			return body;

		} catch ( error ) {
			wpInteractivityStudioDisplayApiConnectionIssueScreen();
		}
	}

	/**
	 * Retrieves an interactive image's svg definition.
	 *
	 * @since      1.0.0
	 * @access     private
	 * 
	 * @memberof wpSlicpixInteractivityStudio
	 * 
	 * @see wpInteractivityStudioDisplayWpRestApiIssueScreen
	 * @see wpInteractivityStudioDisplayApiConnectionIssueScreen
	 */
	async function fetchInteractive() {
		const _ep = epInteractive.replace( '{account}', _account ).replace( '{asset}', _asset );
		try {
			const response = await window.fetch( _ep, {
				method: 'GET',
				headers: {
					'content-type': 'image/svg+xml',
				}
			} );
	
			if ( !response.ok ) {
				wpInteractivityStudioDisplayApiIssueScreen();
				return null;
			}
	
			const body = await response.text();
			return body;

		} catch ( error ) {
			wpInteractivityStudioDisplayApiConnectionIssueScreen();
		}
	}

	/**
	 * Generates the interactive image svg asset.
	 *
	 * Builds the interactive image's svg definition, display the interactive image's
	 * image at the location of the custom block and add o the custom block attributes
	 * the entry interactiveImage with for value the built svg.
	 *  
	 * @since      1.0.0
	 * @access     private
	 * 
	 * @memberof wpSlicpixInteractivityStudio
	 * 
	 * @see fetchArtwork
	 * @see wpInteractivityStudioDisplayApiIssueScreen
	 */
	async function buildInteractiveContent() {

		_insertionStatusMsg.innerText = 'inserting interactive image onto your page...';

		setTimeout( function () {

			try {
				fetchArtwork().then( ( art ) => {

					/* generate the main wrapper element */
					const wrapper = document.createElement( 'div' );
					wrapper.setAttribute( 'class', 'spix-interactivity-container' );
					wrapper.setAttribute( 'data-is-image', 'true' );
					wrapper.setAttribute( 'data-is-account', _account );
					wrapper.setAttribute( 'data-is-third-party', 'wordpress' );
					wrapper.setAttribute( 'data-is-instanceid', wpSlicpixInteractivityStudioCreateUUID() );
					wrapper.setAttribute( 'data-is-asset', _asset );

					const dataSvg = _interactiveData;

					/* generate the svg element */
					const svg = document.createElement( 'svg' );
					svg.setAttribute( 'role', 'img' );
					svg.setAttribute( 'xmlns', 'http://www.w3.org/2000/svg' );
					svg.setAttribute( 'xmlns:xlink', 'http://www.w3.org/1999/xlink' );
					svg.setAttribute( 'id', dataSvg.getAttribute( 'id' ) );
					svg.setAttribute( 'viewbox', dataSvg.getAttribute( 'viewBox' ) );
					svg.setAttribute( 'class', dataSvg.getAttribute( 'class' ) );
					svg.setAttribute( 'pointer-events', 'visible' );
					svg.setAttribute( 'tabindex', '0' );

					if ( art !== null ) {
						svg.setAttribute( 'title', art.title );
						svg.setAttribute( 'aria-label', art.title );
					} else {
						svg.setAttribute( 'title', 'interactive image' );
						svg.setAttribute( 'aria-label', 'interactive image' );
					}
					svg.setAttribute( 'width', '100%' );
					svg.setAttribute( 'height', '100%' );
					svg.setAttribute( 'style', 'height: inherit !important; display: block !important;' );

					/* append the svg element to the main wrapper element */
					wrapper.appendChild( svg );

					/* generate the svg style element */
					const style = document.createElement( 'style' );
					const dataStyle = dataSvg.querySelector( 'style' );
					let css = dataStyle.innerHTML + ' .area .zone path{stroke-width: 0; stroke: none; stroke-opacity: 0;}';
					css = css + ' .spix-interactivity-container{display: block !important; background-color: transparent !important; border: none !important; text-decoration: none !important; cursor:pointer !important;}';
					style.innerHTML = css;

					/* append the svg style element to the svg element */
					svg.appendChild( style );

					/* generate the svg image element */
					const image = document.createElementNS( 'http://www.w3.org/2000/svg', 'image' );
					const dataImage = dataSvg.querySelector( "image" );
					image.setAttribute( 'crossorigin', 'anonymous' );
					image.setAttribute( 'width', dataImage.getAttribute( 'width' ) );
					image.setAttribute( 'height', dataImage.getAttribute( 'height' ) );
					image.setAttribute( 'visibility', 'visible' );
					image.setAttribute( 'href', _interactiveImage );

					/* append the svg image element to the svg element */
					svg.appendChild( image );

					/* generate the svg main group element */
					const ggroup = document.createElementNS( 'http://www.w3.org/2000/svg', 'g' );
					const dataGgroup = dataSvg.querySelector( '[class="group"]' );
					ggroup.setAttribute( 'class', 'group' );

					/* append the svg main group element to the svg element */
					svg.appendChild( ggroup );

					/* collect all a elements */
					const dataLinks = dataGgroup.querySelectorAll( 'a' );
					let dataLink;

					/* loop through all a elements */
					for( let i = 0; i < dataLinks.length; i++ ) {

						/* generate the svg a element */
						const link = document.createElement( 'a' );
						dataLink = dataLinks[ i ];
						
						/* generate the svg interactive zone group element */
						const group = document.createElementNS( 'http://www.w3.org/2000/svg', 'g' );
						const dataGroup = dataLink.querySelector( 'g' );
						group.setAttribute( 'data-spix-id', dataGroup.getAttribute( 'data-spix-id' ) );
						group.setAttribute( 'data-spix-name', dataGroup.getAttribute( 'data-spix-name' ) );
						group.setAttribute( 'class', dataGroup.getAttribute( 'class' ) );
						group.setAttribute( 'tabindex', dataGroup.getAttribute( 'tabindex' ) );
						group.setAttribute( 'role', dataGroup.getAttribute( 'role' ) );
						group.setAttribute( 'aria-label', dataGroup.getAttribute( 'aria-label' ) );

						/* append the svg interactive zone group element to the a element */
						link.appendChild( group );
						const dataPaths = dataGroup.querySelectorAll( 'path' );
						let dataPath;
						/* loop through all path elements of the group element */
						for ( let j = 0; j < dataPaths.length; j++ ) {
							/* generate the svg path element */
							const path = document.createElementNS( 'http://www.w3.org/2000/svg', 'path' );
							dataPath = dataPaths[ j ];
							path.setAttribute( 'data-spix-zone', dataPath.getAttribute( 'data-spix-zone' ) );
							path.setAttribute( 'data-spix-name', dataPath.getAttribute( 'data-spix-name' ) );
							path.setAttribute( 'd', dataPath.getAttribute( 'd' ) );
							path.setAttribute( 'data-spix-resource', dataPath.getAttribute( 'data-spix-resource' ) );
							path.setAttribute( 'data-spix-type', dataPath.getAttribute( 'data-spix-type' ) );
							if ( dataPath.getAttribute( 'data-scope' ) ) {
								path.setAttribute( 'data-scope', dataPath.getAttribute( 'data-scope' ) );
							}

							/* append the svg path element to the svg interactive zone group element */
							group.appendChild( path );
						}

						/* append the svg a element to the svg main group element */
						ggroup.appendChild( link );
					}

					/* generate the svg background group element */
					const dataGbkgd = dataGgroup.querySelector( '[class="background"]' );
					const gbkgd = document.createElementNS( 'http://www.w3.org/2000/svg', 'g' );
					gbkgd.setAttribute( 'class', 'background' );

					/* append the svg background group element to the svg element */
					svg.appendChild( gbkgd );

					/* generate the svg background path element */
					const dataGbkgdPath = dataGbkgd.querySelector( 'path' );
					const gbkgdPath = document.createElementNS( 'http://www.w3.org/2000/svg', 'path' );
					gbkgdPath.setAttribute( 'd', dataGbkgdPath.getAttribute( 'd' ) );
					if ( dataGbkgdPath.getAttribute( 'data-scope' ) ) {
						gbkgdPath.setAttribute( 'data-scope', dataGbkgdPath.getAttribute( 'data-scope' ) );
					}

					/* append the svg background path element to the svg background group element */
					gbkgd.appendChild( gbkgdPath );

					/* generate the svg bubbles definition group element */
					const bubblesdef = document.createElementNS( 'http://www.w3.org/2000/svg', 'g' );
					const dataBubblesDef = dataSvg.querySelector( 'bubbles_def' );
					bubblesdef.setAttribute( 'id', 'bubbles_def' );

					/* add to the svg bubbles definition group element a text attribute containing the bubbles definition */
					const re = /\'/gi;
					let defs = dataBubblesDef.innerHTML;
					defs = defs.replace( re, '&#39;' );
					bubblesdef.setAttribute( 'text', defs );

					/* append the svg bubbles definition group element to the svg element */
					svg.appendChild( bubblesdef );

					/* generate the seo element */
					const seo = document.createElement( 'div' );
					seo.setAttribute( 'class', 'spix-seo' );

					let def, textarea, innerDiv, btnA;
					for ( const property in _defs ) {
						def = _defs[ property ];

						if ( def.html !== null && def.html.length > 0 ) {
							textarea = document.createElement( 'textarea' );
							textarea.innerHTML = def.html;

							innerDiv = document.createElement( 'div' );
							innerDiv.innerHTML = textarea.value;

							if ( def.btnLink !== null && def.btnLink.length > 0 ) {
								btnA = document.createElement( 'a' );
								btnA.setAttribute( 'href', def.btnLink );
								if ( def.btnText !== null && def.btnText.length > 0 ) {
									btnA.innerText = def.btnText;
								}
								innerDiv.appendChild( btnA );
							}

							seo.appendChild( innerDiv );
						}
					}

					wrapper.appendChild( seo );

					/* update the custom block instance to display the interactive image's image */
					const navbar = _customBlock.querySelector( '[ class = "navbar" ]' );
					const body = _customBlock.querySelector( '[ class = "body" ]' );
					const embeddedImage = _customBlock.querySelector( '[ class = "embedded-image" ]' );
					const img = embeddedImage.querySelector( 'img' );
					img.setAttribute( 'src', _interactiveImage );
					navbar.setAttribute( 'data-state', 'off' );
					body.setAttribute( 'data-state', 'off' );
					embeddedImage.setAttribute( 'data-state', 'on' );
					_customBlock.setAttribute( 'data-state', 'embedded-image' );

					/* add to the custom block attributes a interactiveImage entry with for value the built svg */
					const div = document.createElement( 'div' );
					div.appendChild( wrapper );
					const block = wp.data.select( 'core/block-editor' ).getBlock( _blockId );

					debugger;
					block.attributes[ 'interactiveImage' ] = div.innerHTML;

					/* close the Interactivity Studio ovelay */
					wpInteractivityStudioClose();
				} );
			} catch ( error ) {
				wpInteractivityStudioDisplayApiIssueScreen();
			}
		}, 2000 );
	}

	/**
	 * Retrieve the WP URL of an interactive image's bubble image.
	 *
	 * @since      1.0.0
	 * @access     private
	 * 
	 * @memberof wpSlicpixInteractivityStudio
	 * 
	 * @see buildInteractiveContent
	 * @see wpInteractivityStudioDisplayApiIssueScreen
	 */
	async function resolveBubbleImagesWpUrl( promises ) {
		try {
			if ( promises.length > 0 ) {
				await Promise.all( promises.map( p => {
					return p;
				} ) ).then( function () {
					buildInteractiveContent();
				} );
			}
		} catch ( error ) {
			let code = '';
			if ( error && error.data ) {
				code = error.data.status + ' - code:' + error.code;
			}
			wpInteractivityStudioDisplayWpRestApiIssueScreen( code );
		}
	}

	/**
	 * Uploads the interactive image's bubble images to WP media gallery.
	 *
	 * @since      1.0.0
	 * @access     private
	 * 
	 * @memberof wpSlicpixInteractivityStudio
	 * 
	 * @see retrieveBubbleUploadedImageUrl
	 * @see resolveBubbleImagesWpUrl
	 * @see buildInteractiveContent
	 * @see wpInteractivityStudioDisplayWpRestApiIssueScreen
	 */
	async function transferBubbleImages( promises ) {
		try {
			if ( promises.length > 0 ) {
				await Promise.all( promises.map( p => {
					return p;
				} ) ).then( function () {
					const bubbleImagePromises = [];
					let def;
					for ( const property in _defs ) {
						def = _defs[ property ];
						if ( def.theme === 'image' ) {
							bubbleImagePromises.push( retrieveBubbleUploadedImageUrl( def ) );
						}
					}
					resolveBubbleImagesWpUrl( bubbleImagePromises );
				} );
			} else {
				buildInteractiveContent();
			}
		} catch ( error ) {
			let code = '';
			if ( error && error.data ) {
				code = error.data.status + ' - code:' + error.code;
			}
			wpInteractivityStudioDisplayWpRestApiIssueScreen( code );
		}
	}

	/**
	 * Uploads an interactive image's image to WP media gallery.
	 *
	 * @since      1.0.0
	 * @access     private
	 * 
	 * @memberof wpSlicpixInteractivityStudio
	 * 
	 * @see retrieveUploadedImageUrl
	 * @see wpInteractivityStudioDisplayWpRestApiIssueScreen
	 */
	async function uploadImageToMediaGallery( imageUrl ) {
		try {
			await wp.apiFetch( {
				path: 'slicpix_interactivity_studio/media_gallery',
				method: 'POST',
				headers: { 'Content-Type': 'application/json', },
				data: { url: imageUrl }
			} ).then( res => {
				retrieveUploadedImageUrl( res );
			} );
		} catch ( error ) {
			let code = '';
			if ( error && error.data ) {
				code = error.data.status + ' - code:' + error.code;
			}
			wpInteractivityStudioDisplayWpRestApiIssueScreen( code );
		}
	}

	/**
	 * Fetch an interactive image's image WP media gallery URL.
	 *
	 * Fetches the WP URL of an uploaded interactive image's image then initiates the
	 * asynchronous process of uploading an interactive image's bubbles images onto WP
	 * media gallery.
	 * 
	 * @since      1.0.0
	 * @access     private
	 * 
	 * @memberof wpSlicpixInteractivityStudio
	 * 
	 * @see fetchInteractive
	 * @see uploadBubbleImageToMediaGallery
	 * @see transferBubbleImages
	 * @see wpInteractivityStudioDisplayWpRestApiIssueScreen
	 */
	async function retrieveUploadedImageUrl( attachment ) {
		try {
			await wp.apiFetch( {
				path: 'slicpix_interactivity_studio/media_gallery/image',
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				data: { 'attachment': attachment }
			} ).then( res => {
				_interactiveImage = res;
				fetchInteractive().then( ( data ) => {
					const div = document.createElement( 'div' );
					div.innerHTML = data;
					_interactiveData = div.querySelector( 'svg' ); 

					_dataBubblesDef = _interactiveData.querySelector( 'bubbles_def' );
					const bubbles = JSON.parse( _dataBubblesDef.textContent );
					
					let def;
					_defs = bubbles.defs;

					const bubbleImagePromises = []
					
					for ( const property in _defs ) {
						def = _defs[ property ];
						if ( def.theme === 'image' ) {
							bubbleImagePromises.push( uploadBubbleImageToMediaGallery( def ) );
						}
					}
					transferBubbleImages( bubbleImagePromises );
				} );
			} )
		} catch ( error ) {
			let code = '';
			if ( error && error.data ) {
				code = error.data.status + ' - code:' + error.code;
			}
			wpInteractivityStudioDisplayWpRestApiIssueScreen( code );
		}
	}

	/**
	 * Uploads a bubble image to WP media gallery.
	 *
	 * @since      1.0.0
	 * @access     private
	 *
	 * @memberof wpSlicpixInteractivityStudio
	 *
	 * @param {Object} def Bubble definition
	 */
	const uploadBubbleImageToMediaGallery = ( def ) => {
		return wp.apiFetch( {
			path: 'slicpix_interactivity_studio/media_gallery',
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			data: { url: def.image },
		} ).then( ( res ) => {
			def.attachment = res;
		} ).catch ( ( error ) => {
			throw error;
		} );
	};

	/**
	 * Retrieves a bubble image's WP media gallery URL.
	 *
	 * @since      1.0.0
	 * @access     private
	 *
	 * @memberof wpSlicpixInteractivityStudio
	 *
	 * @param {Object} def Bubble definition model
	 */
	const retrieveBubbleUploadedImageUrl = ( def ) => {
		return wp.apiFetch( {
			path: 'slicpix_interactivity_studio/media_gallery/image',
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			data: { attachment: def.attachment },
		} ).then( ( res ) => {
			def.image = res;			
		} ).catch ( ( error ) => {
			throw error;
		} );
	};

	/**
	 * Connects the custom block to an Interactivity Studio account.
	 *
	 * With the installation key fetched from the WP Options table, retrieves
	 * an Interactivity Studio account information then initiates the display
	 * of the account's interactive images.
	 *
	 * @since      1.0.0
	 * @access     private
	 *
	 * @memberof wpSlicpixInteractivityStudio
	 *
	 * @see wpInteractivityStudioMyImages
	 * @see wpInteractivityStudioDisplayApiIssueScreen
	 */
	const loadContext = function () {
		try {
			connect( _apikey ).then( ( data ) => {
				if ( data !== null ) {
					if ( data.account === null || data.account.trim().length === 0 ) {
						wpInteractivityStudioDisplayApiIssueScreen();
						return;
					}
					_account = data.account;
					_gallery = new wpInteractivityStudioMyImages();
					_gallery.load();
				} else {
					wpInteractivityStudioDisplayApiIssueScreen();
				}
			} );
		} catch ( error ) {}
	};

	/**
	 * Generates a UUID.
	 *
	 * @since      1.0.0
	 * @access     private
	 *
	 * @memberof wpSlicpixInteractivityStudio
	 */
	const wpSlicpixInteractivityStudioCreateUUID = () => {
		let dt = new Date().getTime();
		const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace( /[xy]/g , function( c ) {
		const r = ( dt + Math.random() * 16 ) % 16 | 0;
		dt = Math.floor( dt / 16 );
		return ( c=='x' ? r :(r&0x3|0x8) ).toString( 16 );
		} );
		return uuid;
	};

	/**
	 * Closes the Interactivity Studio overlay.
	 *
	 * @since      1.0.0
	 * @access     private
	 *
	 * @memberof wpSlicpixInteractivityStudio
	 */
	const wpInteractivityStudioClose = function () {
		const body = document.querySelector( 'body' );
		const dialog = body.querySelector( '[id="wp-block-slicpix-interactivity-studio-dialog"]' );
		if ( dialog !== null ) {
			dialog.removeAttribute( 'data-state' );
		}
	};

	/**
	 * Closes the Interactivity Studio overlay.
	 *
	 * @since      1.0.0
	 * @access     private
	 *
	 * @memberof wpSlicpixInteractivityStudio
	 *
	 * @see wpInteractivityStudioClose
	 *
	 * @listens event:click
	 *
	 * @param {Event} e Event
	 */
	const onWpInteractivityStudioCloseDialog = ( e ) => {
		e.preventDefault();
		e.stopPropagation();
		wpInteractivityStudioClose();
	};

	/**
	 * Closes the Interactivity Studio overlay.
	 *
	 * @since      1.0.0
	 * @access     private
	 *
	 * @memberof wpSlicpixInteractivityStudio
	 *
	 * @see wpInteractivityStudioClose
	 *
	 * @listens event:click
	 *
	 * @param {Event} e Event
	 */
	const onWpInteractivityStudioCloseDialogWrapper = ( e ) => {
		const dialog = document.querySelector( '[id="wp-block-slicpix-interactivity-studio-dialog"]' );
		const wrapper = dialog.querySelector( '[class="wrapper"]' );
		if ( e.srcElement !== wrapper ) {
			return;
		}
		e.preventDefault();
		e.stopPropagation();
		wpInteractivityStudioClose();
	};

	/**
	 * Generates and displays the Interactivity Studio authentication overlay.
	 *
	 * @since      1.0.0
	 * @access     private
	 *
	 * @memberof wpSlicpixInteractivityStudio
	 * 
	 * @see wpInteractivityStudioSignin
	 */
	const wpInteractivityStudioLoadSignInScreen = function () {
		const div = document.createElement( 'div' );
		div.innerHTML = loginTmpl;
		const body = document.querySelector( 'body' );
		const dialog = body.querySelector( '[id="wp-block-slicpix-interactivity-studio-dialog"]' );
		const contentDg = dialog.querySelector( '[id="content-dg"]' );
		contentDg.innerHTML = '';
		contentDg.appendChild( div.querySelector( 'div' ) );

		const signin = new wpInteractivityStudioSignin();
		signin.display();
	};

	/**
	 * Generates the Interactivity Studio overlay and fetches the installation key.
	 *
	 * @since      1.0.0
	 * @access     private
	 *
	 * @memberof wpSlicpixInteractivityStudio
	 *
	 * @see wpInteractivityStudioFetchKey
	 */
	const onWpInteractivityStudioViewMyImagesClick = ( e ) => {
		_blockId = wp.data.select( 'core/block-editor' ).getSelectedBlockClientId();
		let source;
		if ( e.srcElement !== null && e.srcElement !== undefined ) {
			source = e.srcElement;
		} else {
			source = e.target;
		}

		if ( source === null ) {
			return;
		}

		while ( source.nodeName !== 'DIV' ) {
			source = source.parentElement;
		}

		if ( source.getAttribute( 'class' ) !== 'body' ) {
			return;
		}

		_customBlock = source.parentElement;

		const body = document.querySelector( 'body' );
		let dialog = body.querySelector( '[id="wp-block-slicpix-interactivity-studio-dialog"]' );

		if ( dialog === null ) {
			let link = document.createElement( 'link' );
			link.setAttribute( 'href', 'https://fonts.googleapis.com/css2?family=Chivo:ital,wght@0,100..900;1,100..900&display=swap' );
			link.setAttribute( 'rel', 'stylesheet' );
			body.appendChild( link );

			link = document.createElement( 'link' );
			link.setAttribute( 'href', 'https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100..900;1,100..900&family=Chivo:ital,wght@0,100..900;1,100..900&display=swap' );
			link.setAttribute( 'rel', 'stylesheet' );
			body.appendChild( link );

			dialog = document.createElement( 'div' );
			dialog.setAttribute( 'id', 'wp-block-slicpix-interactivity-studio-dialog' );
			body.appendChild( dialog );

			const wrapper = document.createElement( 'div' );
			wrapper.setAttribute( 'class', 'wrapper' );
			dialog.appendChild( wrapper );

			wrapper.addEventListener( 'click', onWpInteractivityStudioCloseDialogWrapper, false );

			const closeDg = document.createElement( 'div' );
			closeDg.setAttribute( 'class', 'close-dg' );
			closeDg.addEventListener( 'click', onWpInteractivityStudioCloseDialog, false );
			wrapper.appendChild( closeDg );

			const btn = document.createElement( 'button' );
			btn.setAttribute( 'class', 'button' );
			closeDg.appendChild( btn );

			btn.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" version="1.1" aria-hidden="false"><desc lang="en-US">An X shape</desc><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41Z"></path></svg>';

			const contentDg = document.createElement( 'div' );
			contentDg.setAttribute( 'id', 'content-dg' );

			wrapper.appendChild( contentDg );
		}
		
		wpInteractivityStudioFetchKey();
	};

	/**
	 * Manages the Interactivity Studio's interactive images gallery overlay.
	 *
	 * @since      1.0.0
	 * @access     private
	 *
	 * @memberof wpSlicpixInteractivityStudio
	 */
	const wpInteractivityStudioMyImages = function () {

		const masonryBrickTmpl = `<div class='masonry-brick'>
			<div class='masonry-content'>
				<img src='{src}' loading='lazy'>
			</div>
		</div>`;

		const galleryTmpl = `<div class="gallery">
			<div class="gmain" tabindes="-1">
				<div id="masonry" tabindex="-1"></div>
			</div>
		</div>`;

		const noImagesTmpl = `<div class="empty">
			<div class="centerer">
        		<p>You have no interactive images yet</p>
        		<div class="button-ctn">
          			<a href="https://interactivity.studio" target="_blank">
            			<button class="button" data-state="active" tabIndex="0">Create one</button>
          			</a>
        		</div>
      		</div>
		</div>`;

		const insertingTmpl = `<div class="loader">
			<svg class="ip" viewBox="0 0 256 128" width="256px" height="128px" xmlns="http://www.w3.org/2000/svg">
				<defs>
				<linearGradient id="grad1" x1="0" y1="0" x2="1" y2="0">
					<stop offset="0%" stop-color="#8554FA" />
					<stop offset="50%" stop-color="#BB9FFF" />
					<stop offset="100%" stop-color="#9966FE" />
				</linearGradient>
				<linearGradient id="grad2" x1="1" y1="0" x2="0" y2="0">
					<stop offset="0%" stop-color="#8554FA" />
					<stop offset="50%" stop-color="#BB9FFF" />
					<stop offset="100%" stop-color="#9966FE" />
				</linearGradient>
				</defs>
				<g fill="none" stroke-linecap="round" stroke-width="16">
					<g class="ip__track" stroke="#ddd">
						<path d="M8,64s0-56,60-56,60,112,120,112,60-56,60-56"/>
						<path d="M248,64s0-56-60-56-60,112-120,112S8,64,8,64"/>
					</g>
					<g stroke-dasharray="180 656">
						<path class="ip__worm1" stroke="url(#grad1)" stroke-dashoffset="0" d="M8,64s0-56,60-56,60,112,120,112,60-56,60-56"/>
						<path class="ip__worm2" stroke="url(#grad2)" stroke-dashoffset="358" d="M248,64s0-56-60-56-60,112-120,112S8,64,8,64"/>
					</g>
				</g>
			</svg>
			<div id="wp-block-slicpix-interactivity-studio-dialog-insertion-status-msg" class="text">inserting ...</div>
		</div>`;

		/**
		 * Resizes an interactive images gallery's item.
		 *
		 * @since      1.0.0
		 * @access     private
		 *
		 * @memberof wpSlicpixInteractivityStudio
		 *
		 * @listens event:load
		 *
		 * @param {Event} e	Event object
		 */
		const resizeMasonryItem = ( e ) => {
			let img;
			if ( e.srcElement !== null ) {
				img = e.srcElement;
				img.removeEventListener( 'load', resizeMasonryItem, false );
			} else {
				img = e.querySelector( 'img' );
			}
			
			const item = img.item;
			const rowGap = parseInt( window.getComputedStyle( _grid ).getPropertyValue( 'grid-row-gap' ) );
			const rowHeight = parseFloat( window.getComputedStyle( _grid ).getPropertyValue( 'grid-auto-rows' ) );
		
			const contentHeight = item.querySelector( '.masonry-content' ).getBoundingClientRect().height;
			const rowSpan = Math.round( ( contentHeight + rowGap ) / ( rowHeight + rowGap ) );
			
			item.style.gridRowEnd = 'span ' + rowSpan;
		};
		
		/**
		 * Resizes all interactive images gallery's items.
		 *
		 * @since      1.0.0
		 * @access     private
		 *
		 * @memberof wpSlicpixInteractivityStudio
		 *
		 * @see resizeMasonryItem
		 *
		 * @listens event:resize
		 */
		const resizeAllMasonryItems = () => {
			const allItems = _grid.querySelectorAll( '[class="masonry-brick"]' );
			for ( let i = 0; i < allItems.length; i++ ) {
				resizeMasonryItem( allItems[ i ] );
			}
		};
	
		/**
		 * Initiates the embedding process of an interactive image.
		 *
		 * @since      1.0.0
		 * @access     private
		 *
		 * @memberof wpSlicpixInteractivityStudio
		 *
		 * @see uploadImageToMediaGallery
		 *
		 * @listens event:click
		 */
		const onGridItemSelected = ( e ) => {
			e.preventDefault();
			e.stopPropagation();

			let source;
			if ( e.srcElement !== null ) {
				source = e.srcElement;
			} else {
				source = e.target;
			}

			if ( source === null || source.getAttribute( 'class' ) === 'masonry' ) {
				return;
			}

			while ( source.getAttribute( 'class' ) !== 'masonry-brick' ) {
				source = source.parentElement;
			}

			_asset = source.getAttribute( 'id' );

			const img = source.querySelector( 'img' );
			const src = img.getAttribute( 'src' );

			uploadImageToMediaGallery( src );

			const div = document.createElement( 'div' );
			div.innerHTML = insertingTmpl;
			_insertionStatusMsg = div.querySelector( '[id="wp-block-slicpix-interactivity-studio-dialog-insertion-status-msg"]' );
			_insertionStatusMsg.innerText = "fetching and saving interactive image's assets to your media gallery...";
			
			const body = document.querySelector( 'body' );
			const dialog = body.querySelector( '[id="wp-block-slicpix-interactivity-studio-dialog"]' );
			const contentDg = dialog.querySelector( '[id="content-dg"]' );
			contentDg.innerHTML = '';
			contentDg.appendChild( div.querySelector( 'div' ) );

		};

		/**
		 * reloads the interactive images gallery.
		 *
		 * @since      1.0.0
		 * @access     private
		 *
		 * @memberof wpSlicpixInteractivityStudio
		 *
		 * @listens event:click
		 */
		const onRefresh = ( e ) => {
			e.preventDefault();
			e.stopPropagation();
			load();
		};

		/**
		 * Loads the interactive images gallery overlay.
		 *
		 * @since      1.0.0
		 * @access     private
		 *
		 * @memberof wpSlicpixInteractivityStudio
		 *
		 * @see fetchArtworks
		 */
		const load = function () {

			fetchArtworks().then( ( data ) => {
				const div = document.createElement( 'div' );
				div.innerHTML = myImagesTmpl;

				const footerDiv = document.createElement( 'div' );
				footerDiv.innerHTML = myImagesFooterTmpl;
				
				const body = document.querySelector( 'body' );
				const dialog = body.querySelector( '[id="wp-block-slicpix-interactivity-studio-dialog"]' );
				const contentDg = dialog.querySelector( '[id="content-dg"]' );
				contentDg.innerHTML = '';
				contentDg.appendChild( div.querySelector( 'div' ) );
				contentDg.appendChild( footerDiv.querySelector( 'div' ) );

				const refresh = contentDg.querySelector( '[id="slicpix-interactivity-studio-refresh"]' );
				refresh.removeEventListener( 'click', onRefresh, false);
				refresh.addEventListener( 'click', onRefresh, false);

				const main = contentDg.querySelector( '[id = "main"]' );

				if ( data !== null && data.artworks !== null && data.artworks.length > 0 ) {

					div.innerHTML = galleryTmpl;
					main.innerHTML = '';
					main.appendChild( div.querySelector( 'div' ) );

					_grid = main.querySelector( '[id="masonry"]' );
					let entry, parts, cardDiv, markup;
					const items = data.artworks;
					for (let i = items.length - 1; i >= 0; i-- ) {
						entry = items[ i ];
						parts = entry.split( '::' );
						markup = masonryBrickTmpl.replace( '{src}' , _domainUri + 'content/image/' + parts[ 0 ] + '/' + parts[ 1 ] + '/thumb' + parts[ 1]  + '.webp' );
						cardDiv = document.createElement( 'div' );
						cardDiv.innerHTML = markup;
						const card = cardDiv.querySelector( 'div' );
						card.setAttribute( 'id', parts[ 1 ] );
						card.setAttribute( 'data-handle', parts[ 2 ] );
						card.setAttribute( 'data-slug', parts[ 3 ] );
						card.setAttribute( 'tabindex', '0' );
						const img = card.querySelector( 'img' );
						img.item = card;
						img.addEventListener( 'load', resizeMasonryItem, false );
						_grid.appendChild( card );
					}

					_grid.addEventListener( 'click', onGridItemSelected, false );

					const masonryEvents = [ 'resize' ];
					masonryEvents.forEach( function ( event ) {
						window.addEventListener( event, resizeAllMasonryItems );
					} );
				} else {
					div.innerHTML = noImagesTmpl;
					main.innerHTML = '';
					main.appendChild( div.querySelector( 'div' ) );
				}

				dialog.setAttribute( 'data-state', 'on' );
			} );

		};

		/**
		 * Loads the Interactivity Studio's interactive images gallery overlay.
		 *
		 * @since      1.0.0
		 * @access     public
		 *
		 * @memberof wpSlicpixInteractivityStudio
		 */
		this.load = function () {
			load();
		}; 
	};

	/**
	 * Manages the Interactivity Studio's authentication overlay.
	 *
	 * @since      1.0.0
	 * @access     private
	 *
	 * @memberof wpSlicpixInteractivityStudio
	 */
	const wpInteractivityStudioSignin = function () {
		const scope = {};

		/**
		 * Transitions to the display of the interactive images in the overlay.
		 *
		 * @since      1.0.0
		 * @access     private
		 *
		 * @memberof wpSlicpixInteractivityStudio
		 *
		 * @see wpInteractivityStudioMyImages
		 */
		const onGoOn = function ( e ) {
			e.preventDefault();
			e.stopPropagation();

			_gallery = new wpInteractivityStudioMyImages();
			_gallery.load();

		};

		/**
		 * Manages the authentcation process email sending step.
		 *
		 * @since      1.0.0
		 * @access     private
		 *
		 * @memberof wpSlicpixInteractivityStudio
		 *
		 * @see wpInteractivityStudioSendVerificationEmail
		 */
		const emailVerification = function () {

			wpInteractivityStudioSendVerificationEmail( scope ).then( ( data ) => {
				if ( data !== null ) {
					scope.session = data.session;
					
					scope.feedback.removeAttribute( 'data-state' );
					scope.feedback.innerText = '';

					scope.btn.setAttribute( 'data-state', 'active' );
					scope.btn.removeAttribute( 'data-disabled' );
					scope.btn.emailSent = true;
					scope.btn.InnerText = 'Next';

					scope.emailField.removeAttribute( 'data-state' );
					scope.codeField.setAttribute( 'data-state', 'on' );

					setTimeout( function () {
						scope.code.focus();
					} , 250 );
				}
			} );
		};

		/**
		 * Manages the authentcation process code verification step.
		 *
		 * @since      1.0.0
		 * @access     private
		 *
		 * @memberof wpSlicpixInteractivityStudio
		 *
		 * @see wpSlicpixInteractivityStudioCreateUUID
		 * @see wpInteractivityStudioSendVerificationCode
		 * @see wpInteractivityStudioDeleteKey
		 * @see wpInteractivityStudioDisplayWpRestApiIssueScreen
		 */
		const codeVerification = async () => {
			
			const siteKey = wpSlicpixInteractivityStudioCreateUUID();

			try {
				await wp.apiFetch( {
					path: 'slicpix_interactivity_studio/key',
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					data: { "key": siteKey }
				} ).then( ( res ) => {
					_apikey = res;

					wpInteractivityStudioSendVerificationCode( scope ).then( ( data ) => {

						if (data !== null && data !== 'conflict' && data.account.length > 0 ) {

							_account = data.account;

							const div = document.createElement( 'div' );
							div.innerHTML = setupSuccessTmpl;
							
							const body = document.querySelector( 'body' );
							const dialog = body.querySelector( '[id="wp-block-slicpix-interactivity-studio-dialog"]' );
							const contentDg = dialog.querySelector( '[id="content-dg"]' );
							contentDg.innerHTML = '';
							contentDg.appendChild( div.querySelector( 'div' ) );

							const goOn = dialog.querySelector( '[id="wp-continue"]' );
							goOn.addEventListener( 'click', onGoOn, false );
							goOn.addEventListener( 'keypress', onGoOn, false );

						} else {

							wpInteractivityStudioDeleteKey( function () {
								const div = document.createElement( 'div' );
								if ( data !== 'conflict' ) {
									div.innerHTML = setupFailureTmpl;
								} else {
									div.innerHTML = setupConflictTmpl;
								}
								
								const body = document.querySelector( 'body' );
								const dialog = body.querySelector( '[id="wp-block-slicpix-interactivity-studio-dialog"]' );
								const contentDg = dialog.querySelector( '[id="content-dg"]' );
								contentDg.innerHTML = '';
								contentDg.appendChild( div.querySelector( 'div' ) );

								const close = dialog.querySelector( '[id="wp-close"]' );
								close.addEventListener( 'click', onWpInteractivityStudioCloseDialog, false );
								close.addEventListener( 'keypress', onWpInteractivityStudioCloseDialog, false );
							} );
						}
					} );
				} );
			} catch ( error ) {
				let code = '';
				if ( error && error.data ) {
					code = error.data.status + ' - code:' + error.code;
				}

				wpInteractivityStudioDisplayWpRestApiIssueScreen( code );
			}
		};
		
		/**
		 * Manages the transition between email sending and code verification steps.
		 *
		 * @since      1.0.0
		 * @access     private
		 *
		 * @memberof wpSlicpixInteractivityStudio
		 *
		 * @see emailVerification
		 * @see codeVerification
		 *
		 * @listens event:click
		 * @listens event:keypress
		 */
		const onNext = function ( e ) {
			if ( ( e.keyCode !== null && e.keyCode === 13 ) || ( e.type === "click" ) ) {
				if ( !scope.btn.emailSent ) {
					if ( scope.email.value.trim().length === 0 ) {
						scope.email.value = '';
						scope.feedback.innerText = 'an email address is required';
						scope.feedback.setAttribute( 'data-state', 'on' );
						scope.email.focus();
						return;
					}
					scope.btn.removeAttribute( 'data-state', 'active' );
					scope.btn.setAttribute( 'data-disabled', 'true' );
					emailVerification();
				} else {
					if ( scope.code.value.trim().length === 0 ) {
						scope.feedback.innerText = 'a verification code is required';
						scope.feedback.setAttribute( 'data-state', 'on' );
						scope.code.focus();
						return;
					}
					scope.btn.removeAttribute( 'data-state', 'active' );
					scope.btn.setAttribute( 'data-disabled', 'true' );
					codeVerification();
				}
			}
		};
		
		/**
		 * Check email's input validity.
		 *
		 * @since      1.0.0
		 * @access     private
		 *
		 * @memberof wpSlicpixInteractivityStudio
		 *
		 * @listens event:input
		 */
		const onInputEmail = function () {

			if ( scope.email.value.trim().length > 0 ) {
				scope.btn.removeAttribute( 'data-disabled' );
				scope.btn.setAttribute( 'data-state', 'active' );
			} else {
				scope.btn.setAttribute( 'data-disabled', 'true' );
				scope.btn.removeAttribute( 'data-state' );
			}
		};
		
		/**
		 * Displays the Interactivity Studio's authentication overlay.
		 *
		 * @since      1.0.0
		 * @access     private
		 *
		 * @memberof wpSlicpixInteractivityStudio
		 */
		const display = function () {

			scope.signin = document.querySelector( '[id="wp-signin"]' );
			scope.feedback = scope.signin.querySelector( '[class="feedback"]' );
			scope.feedback.innerText = '';
			scope.btn = scope.signin.querySelector( '[class="button"]' );
			scope.btn.setAttribute( 'data-disabled', 'true' );
			scope.btn.removeAttribute( 'data-state' );
			
			scope.emailField = scope.signin.querySelector( '[class~="email-signin"]' );
			scope.email = scope.emailField.querySelector( 'input' );
			scope.email.addEventListener( 'input', onInputEmail, false );
			scope.codeField = scope.signin.querySelector( '[class~="verificationcode-signin"]' );
			scope.codeField.removeAttribute( 'data-state' );
			scope.code = scope.codeField.querySelector( 'input' );
			scope.code.value = '';

			scope.feedback.removeAttribute( 'data-state' );
			scope.emailField.setAttribute( 'data-state', 'on' );

			setTimeout( function () {
				scope.email.focus();
			}, 250 );

			scope.email.addEventListener( 'keypress', onNext, false );
			scope.code.addEventListener( 'keypress', onNext, false );
			
			scope.btn.addEventListener( 'click', onNext, false );
			scope.btn.addEventListener( 'keypress', onNext, false );

		};
		
		/**
		 * Displays the Interactivity Studio's authentication overlay.
		 *
		 * @since      1.0.0
		 * @access     public
		 *
		 * @memberof wpSlicpixInteractivityStudio
		 */
		this.display = function () {
			display();
		};
	};

	ns.onWpInteractivityStudioViewMyImagesClick = onWpInteractivityStudioViewMyImagesClick;

}) (window.wpSlicpixInteractivityStudio = window.wpSlicpixInteractivityStudio || {} )

/**
 * The edit function describes the structure of the slicpix interactivity
 * studio block in the context of the editor. 
 * This represents what the editor renders when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @return {Element} Element to render.
 * 
 * @param {Object} props The custom block properties
 */
export default function Edit( props ) {
	const { attributes, setAttributes } = props;
    const { interactiveImage } = attributes;
	
	let interactiveImageUrl = null;

	if ( interactiveImage ) {
		const div = document.createElement( 'div' );
		div.innerHTML = interactiveImage;
		const svg = div.querySelector( 'svg' );
		const image = svg.querySelector( 'image' );
		interactiveImageUrl = image.getAttribute( 'href' );
		setAttributes( { interactiveImageUrl: interactiveImageUrl } );
	}

	if ( interactiveImageUrl === null || interactiveImageUrl === undefined || interactiveImageUrl.length === 0 ) {

		return (
			<>
				<div { ...useBlockProps() }>
					
					<nav className="navbar" >
						<a className="navbar-brand">
							<img src="https://interactivity.studio/images/interactivity_studio_logo.webp" alt="interactivity studio logo" className="logo-img" />
							<span>Interactive Images</span>
						</a>
					</nav>
					<div className="body" >
						<p>Click &apos;view my images&apos; and select an interactive image to embed on your website</p>
						<Button variant="primary" onClick={ window.wpSlicpixInteractivityStudio.onWpInteractivityStudioViewMyImagesClick } >view my images</Button>
					</div>
					
					<div className="embedded-image" >
						<img src={ attributes.interactiveImageUrl } alt="interactive image"/>
					</div>
					
				</div>
			</>
		);
	}

	return (
		<>
			<div { ...useBlockProps() }>
				<div className="embedded-image" data-state="on" >
					<img src={ attributes.interactiveImageUrl }	alt="interactive image"/>
				</div>
			</div>
		</>
	);
}
